import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"
import closeButton from '../../assets/images/close.png'

import { createAgreement, getAgreementList, updateAgreement } from "../../services/AppService";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import AddAgreementForm from "../../components/dashboard/Prceedings/AggreementProcess/AddAgreementForm";
import ViewAgreementDetailsModal from "../../components/dashboard/Prceedings/AggreementProcess/ViewAgreementDetailsModal";
import { useTranslation } from "react-i18next";

const AggreementProcess = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [agreements, setAgreements] = useState([]);
    const [editingItem, setEditingItem] = useState(null);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedAgreement, setSelectedAgreement] = useState(null);

    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const [confirmModal, setConfirmModal] = useState({ open: false, formData: null });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const showSuccess = (msg) => setMessageModal({ open: true, type: "success", title: "Success", message: msg });
    const showError = (msg) => setMessageModal({ open: true, type: "error", title: "Error", message: msg });

    useEffect(() => {
        const timer=setTimeout(()=>setDebouncedSearch(search), 500);
        return ()=> clearTimeout(timer);
     }, [search]);

     useEffect(()=>{
        fetchAgreements();
     }, [page, size, debouncedSearch]);

    const fetchAgreements = async () => {
        try {
            setLoading(true);
            const response=await getAgreementList(debouncedSearch, page, size);
            if(response){
                setAgreements(response.content || []);
                setTotalPages(response.totalPages || 0);
                setTotalElements(response.totalElements || 0);
            }
        }catch(error){
            // showError("Failed to load agreements");
            console.error(error);
        }finally{
            setLoading(false);
        }
    };

    const handleFormSubmit=(formData)=>{
        setIsModalOpen(false);
        setConfirmModal({open:true, formData:formData});
    };

    const handleConfirmSave=async()=>{
        const {formData}=confirmModal;
        try{
            if(editingItem){
                await updateAgreement(editingItem.id, formData);
                showSuccess("Agreement updated successfully");
            }else{
                await createAgreement(formData);
                showSuccess("Agreement added successfully");
            }
            fetchAgreements();
            setConfirmModal({open:false, formData:null});
            setEditingItem(null);
        }catch(error){
            showError(error.response?.data?.message || "Internal Server Error", error);
        }
    };

    return(
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t("pageTitles.agreementProcessList")}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={() => { setEditingItem(null); setIsModalOpen(true); }}>
                    {t("common.addNew")}
                </button>
            </div>

            {/* Entries and Search Bar matched with image_fff726.png layout */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t("common.show")}</label>
                    <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2 py-1 text-sm outline-none">
                        {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label className="text-sm">{t("common.entries")}</label>
                </div>
                <input 
                    type="text" 
                    placeholder={t("tables.agreementProcess.search")} 
                    value={search} 
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }} 
                    className="border rounded px-3 py-1 text-sm outline-none w-full sm:w-64" 
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border text-sm ctable rounded-lg">
                    <thead>
                        <tr className="bg-[#a0adf1] text-white">
                            <th className="px-3 py-2 text-center border-b border-black">{t("tables.srNo")}</th>
                            <th className="px-3 py-2 border-b border-black">{t("tables.workName")}</th>
                            <th className="px-3 py-2 border-b border-black">{t("tables.agreementProcess.tenderId")}</th>
                            <th className="px-3 py-2 border-b border-black">{t("tables.agreementProcess.agreementNo")}</th>
                            <th className="px-3 py-2 border-b border-black">BPG</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t("tables.view")}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t("tables.edit")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center py-4">{t("tables.agreementProcess.loadingAgreementProcess")}</td></tr>
                        ) : agreements.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-4 text-gray-500">{t("tables.agreementProcess.noAgreementProcess")}</td></tr>
                        ) : (
                            agreements.map((item, idx) => (
                                <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                                    <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                    <td className="px-3 py-2">{item.workTitle}</td>
                                    <td className="px-3 py-2">{item.tenderId}</td>
                                    <td className="px-3 py-2">{item.agreementNo}</td>
                                    <td className="px-3 py-2">{item.bpg}</td>
                                    <td className="px-3 py-2 text-center">
                                        <button
                                            className="px-3 btnImgView py-1 rounded text-xs cursor-pointer"
                                            onClick={() => {
                                                setSelectedAgreement(item);
                                                setViewModalOpen(true);
                                            }}
                                        ></button>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <button className="px-3 py-1 btnImgEdit rounded text-xs cursor-pointer" onClick={() => { setEditingItem(item); setIsModalOpen(true); }}></button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <p>{t("common.showing")} {page * size + 1} {t("common.to")} {page * size + agreements.length} {t("common.to")} {totalElements}{t("common.entries")}</p>
                <div className="flex gap-2">
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>{t("common.previous")}</button>
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page === totalPages - 1}>{t("common.next")}</button>
                </div>
            </div>

            <AddAgreementForm
                onSubmit={handleFormSubmit}
                editData={editingItem}
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingItem(null); }}
            />

            <ConfirmActionModal
                open={confirmModal.open}
                title={editingItem ? "Confirm Update" : "Confirm Addition"}
                message={`Are you sure you want to ${editingItem ? "update" : "save"} these agreement details?`}
                onConfirm={handleConfirmSave}
                onClose={() => setConfirmModal({ open: false, formData: null })}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />

            <ViewAgreementDetailsModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                agreement={selectedAgreement}
            />
        </div>
    )

}

export default AggreementProcess;